import React from 'react';
import { BookOpen, Calendar, CheckCircle, ExternalLink, FileCheck, Info, Mail, Shield, Users } from 'lucide-react';

const PageShell = ({ title, subtitle, icon: Icon, children }) => (
  <div className="space-y-6">
    <section className="rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 p-6 text-white shadow-xl">
      <div className="flex items-start gap-4">
        <Icon className="h-9 w-9 shrink-0 text-yellow-300" aria-hidden="true" />
        <div>
          <h2 className="text-2xl font-black md:text-3xl">{title}</h2>
          <p className="mt-2 max-w-3xl text-white/90">{subtitle}</p>
        </div>
      </div>
    </section>
    {children}
  </div>
);

const SourceLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-900">
    {children}<ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
  </a>
);

export const EducationUpdatesPage = ({ setPage }) => (
  <PageShell
    title="Jamaica Education Updates"
    subtitle="A practical verification desk for families. Reviewed 11 August 2026; deadlines and notices can change without warning."
    icon={Calendar}
  >
    <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-950">
      <h3 className="flex items-center gap-2 font-bold"><Info className="h-5 w-5" />Before you act</h3>
      <p className="mt-2 text-sm leading-6">We do not label an announcement “current” merely because it appears in a search result. Confirm dates, eligibility and instructions on the responsible agency's website. This page links to the primary authorities rather than republishing unverified social-media posts.</p>
    </section>

    <div className="grid gap-5 md:grid-cols-2">
      {[
        {
          title: 'Ministry notices & school operations',
          body: 'Check official circulars, school-year arrangements, placement guidance, emergency closures and national education announcements.',
          href: 'https://moey.gov.jm/', label: 'Ministry of Education, Skills, Youth and Information'
        },
        {
          title: 'CSEC & CAPE examinations',
          body: 'Use CXC for timetables, candidate notices and results services. Your school or local examinations authority remains the first point of contact for candidate-specific issues.',
          href: 'https://www.cxc.org/', label: 'Caribbean Examinations Council'
        },
        {
          title: 'PEP & secondary placement',
          body: 'Placement and assessment instructions should be confirmed with the Ministry and the child’s school. Never post a candidate number or results slip in a public submission.',
          href: 'https://moey.gov.jm/', label: 'Ministry official portal'
        },
        {
          title: 'TVET, training & certification',
          body: 'Programme availability, entry requirements and application windows vary by institution and intake. Confirm them directly before paying or submitting documents.',
          href: 'https://www.heart-nsta.org/', label: 'HEART/NSTA Trust'
        },
        {
          title: 'Tertiary applications & student finance',
          body: 'Universities set their own admissions calendars. Loan and grant terms may also change between academic years; use the institution and SLB portals for the binding details.',
          href: 'https://www.slbja.com/', label: 'Students’ Loan Bureau'
        },
        {
          title: 'Early childhood institutions',
          body: 'Confirm registration status and current requirements with the national regulator before selecting or paying an early-childhood institution.',
          href: 'https://ecc.gov.jm/', label: 'Early Childhood Commission'
        }
      ].map(item => (
        <article key={item.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
          <p className="my-3 text-sm leading-6 text-slate-600">{item.body}</p>
          <SourceLink href={item.href}>{item.label}</SourceLink>
        </article>
      ))}
    </div>

    <section className="rounded-2xl border border-brand-200 bg-white p-6 shadow-sm">
      <h3 className="font-bold text-slate-900">How freshness is shown</h3>
      <ul className="mt-3 grid gap-3 text-sm text-slate-700 md:grid-cols-3">
        <li className="flex gap-2"><CheckCircle className="h-5 w-5 shrink-0 text-brand-600" />A review date tells you when our team last checked the page.</li>
        <li className="flex gap-2"><FileCheck className="h-5 w-5 shrink-0 text-brand-600" />Primary-source links let you verify the original notice.</li>
        <li className="flex gap-2"><Mail className="h-5 w-5 shrink-0 text-brand-600" />Corrections are moderated before publication.</li>
      </ul>
      <button onClick={() => setPage('suggest-correction')} className="mt-5 rounded-xl bg-brand-700 px-5 py-3 font-semibold text-white hover:bg-brand-800">Report outdated information</button>
    </section>
  </PageShell>
);

export const AboutEditorialPage = ({ setPage }) => (
  <PageShell title="About & Editorial Standards" subtitle="Who publishes this guide, how information is checked, and how commercial relationships are kept separate from education content." icon={BookOpen}>
    <div className="grid gap-5 lg:grid-cols-3">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
        <h3 className="text-xl font-bold">Our purpose</h3>
        <p className="mt-3 leading-7 text-slate-700">Jamaica Schools Guide is an independent education-information platform developed by Johnathon Kelly. It helps students, parents and educators understand pathways, compare schools and find support. It is not a government agency, school, examination body or admissions office.</p>
        <h3 className="mt-6 text-xl font-bold">Editorial process</h3>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
          <li>Prefer ministries, regulators, examination bodies and institutions as primary sources.</li>
          <li>Show source and verification dates where practical; distinguish reported data from community opinion.</li>
          <li>Moderate listings, reviews, achievements and corrections before public use.</li>
          <li>Correct material errors promptly and preserve context around rankings and historical data.</li>
          <li>Do not accept payment for a better school ranking or a favourable editorial conclusion.</li>
        </ul>
      </section>
      <aside className="rounded-2xl border border-brand-200 bg-brand-50 p-6">
        <Shield className="h-8 w-8 text-brand-700" />
        <h3 className="mt-3 text-lg font-bold">Advertising independence</h3>
        <p className="mt-2 text-sm leading-6 text-slate-700">Ads and paid directory tiers fund the platform. They do not determine rankings, exam guidance or editorial coverage. Sponsored material must be identifiable.</p>
      </aside>
    </div>
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="flex items-center gap-2 text-xl font-bold"><Users className="h-6 w-6 text-brand-700" />Corrections and contact</h3>
      <p className="mt-3 text-slate-700">Send the page URL, disputed statement, supporting source and requested correction. Do not send a child's confidential record through a public form.</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button onClick={() => setPage('suggest-correction')} className="rounded-xl bg-brand-700 px-5 py-3 font-semibold text-white hover:bg-brand-800">Suggest a correction</button>
        <button onClick={() => setPage('contact')} className="rounded-xl border border-slate-300 px-5 py-3 font-semibold text-slate-800 hover:bg-slate-50">Contact the publisher</button>
      </div>
    </section>
  </PageShell>
);

export const TermsPage = () => (
  <PageShell title="Terms of Use" subtitle="Rules for using Jamaica Schools Guide. Effective 11 August 2026." icon={Shield}>
    <section className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm">
      <div><h3 className="font-bold text-slate-950">Information, not professional advice</h3><p className="mt-2">Content is general educational information. Confirm admissions, fees, deadlines, results and legal or safeguarding decisions with the responsible authority.</p></div>
      <div><h3 className="font-bold text-slate-950">Acceptable use</h3><p className="mt-2">Do not submit unlawful, deceptive, discriminatory, infringing or malicious content; impersonate another person; scrape or disrupt the service; upload malware; manipulate reviews; or disclose a child's confidential information.</p></div>
      <div><h3 className="font-bold text-slate-950">Community and business submissions</h3><p className="mt-2">You must have the right to provide submitted text, images and business details. We may verify, edit, reject or remove a submission. Publication is not an endorsement, accreditation or guarantee.</p></div>
      <div><h3 className="font-bold text-slate-950">Rankings and third-party links</h3><p className="mt-2">Rankings are contextual indicators, not a definitive measure of school quality. Third-party sites control their own content and availability.</p></div>
      <div><h3 className="font-bold text-slate-950">Availability and changes</h3><p className="mt-2">We may correct, update, suspend or discontinue features. To the extent permitted by law, the service is provided without a guarantee that every item is complete, current or error-free.</p></div>
      <div><h3 className="font-bold text-slate-950">Contact</h3><p className="mt-2">Questions about these terms may be sent to info@jamaicaschoolsguide.com.</p></div>
    </section>
  </PageShell>
);
